/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable react-hooks/exhaustive-deps */

import { useState, useEffect, useRef } from "react";
import { useFieldArray, useForm, Controller } from "react-hook-form";
import { Flex, Heading, Button, Checkbox } from "@chakra-ui/react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { FaEdit } from "react-icons/fa";
import { MdDeleteSweep } from "react-icons/md";

import NumericInput from "../../../components/forms/react-hook-numeric-input";
import ReactHookFormDropdown from "../../../components/forms/react-hook-dropdown";
import { ACTIVITY_TYPES } from "pages/customers/constants";
import { siteSLADefault } from "../constants";

const safeArray = (val) => (Array.isArray(val) ? val : []);

export default function SiteSLAs({
  locationSlaDefaults,
  initialSLAs = [],
  initialFollowsCustomerSla = true,
  addData,
}) {
  const [currentEditing, setCurrentEditing] = useState(null);

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm({
    mode: "all", // ensures validation is checked on change
    defaultValues: {
      SLAs: safeArray(initialSLAs),
      followsCustomerSla: !!initialFollowsCustomerSla,
      activityType: null,
      responseTime: "",
      attendedTime: "",
      resolutionTime: "",
    },
  });

  const { fields: SLAfields, remove } = useFieldArray({
    control,
    name: "SLAs",
  });

  const generalFieldProps = { control, errors };

  // Reset form whenever initialSLAs change
  useEffect(() => {
    reset((prev) => ({
      ...prev,
      SLAs: safeArray(initialSLAs),
      followsCustomerSla: initialFollowsCustomerSla ?? prev.followsCustomerSla,
      activityType: null,
      responseTime: "",
      attendedTime: "",
      resolutionTime: "",
    }));
  }, [initialSLAs, initialFollowsCustomerSla, reset]);

  const selectedActivityType = watch("activityType")?.text;

  useEffect(() => {
    if (!locationSlaDefaults?.sla) return;
    if (!selectedActivityType) return;
    if (currentEditing !== null) return;

    const match = locationSlaDefaults.sla.find(
      (s) => s.activityType === selectedActivityType,
    );

    if (!match) return;

    setValue("responseTime", match.responseTime ?? "");
    setValue("resolutionTime", match.resolutionTime ?? "");
  }, [selectedActivityType, locationSlaDefaults, currentEditing]);

  // Sync with parent
  const watchSLAs = watch("SLAs");
  const watchFollows = watch("followsCustomerSla");
  const prevDataRef = useRef({ SLAs: [], followsCustomerSla: false });

  useEffect(() => {
    const newData = {
      SLAs: safeArray(watchSLAs),
      followsCustomerSla: !!watchFollows,
    };

    // Compare previous data to avoid unnecessary updates
    const prevData = prevDataRef.current;
    if (JSON.stringify(newData) !== JSON.stringify(prevData)) {
      addData && addData(newData);
      prevDataRef.current = newData; // update previous data
    }
  }, [watchSLAs, watchFollows, addData]);

  const formValidationRules = {
    activityType: { required: "Please select Activity Type" },
    responseTime: {
      required: "Please enter Response Time",
      validate: (v) => {
        if (Number(v) <= 0) return "Must be greater than 0";

        const resolutionTime = Number(getValues("resolutionTime"));
        if (resolutionTime && Number(v) >= resolutionTime) {
          return "Response time must be less than resolution time";
        }

        return true;
      },
    },

    // attendedTime: {
    //   required: "Please enter Attended Time",
    //   validate: (v) => Number(v) > 0 || "Must be greater than 0",
    // },

    resolutionTime: {
      required: "Please enter Resolution Time",
      validate: (v) => {
        if (Number(v) <= 0) return "Must be greater than 0";

        const responseTime = Number(getValues("responseTime"));
        if (responseTime && Number(v) <= responseTime) {
          return "Resolution time must be greater than response time";
        }

        return true;
      },
    },
  };

  // Add / Update SLA
  const submitInfo = (data) => {
    const _SLAs = [...safeArray(getValues("SLAs"))];

    const activityType = data?.activityType;

    // DUPLICATE CHECK
    const duplicateExists = _SLAs.some((s, index) => {
      if (currentEditing !== null && index === currentEditing) {
        return false; // ignore row being edited
      }

      return s.activityType === activityType;
    });

    if (duplicateExists) {
      setError("activityType", {
        type: "duplicate",
        message: "This Activity Type already exists in the table",
      });
      return;
    }

    const SLA = {
      activityType,
      responseTime: data.responseTime,
      attendedTime: data.attendedTime,
      resolutionTime: data.resolutionTime,
    };

    if (currentEditing !== null) {
      _SLAs[currentEditing] = SLA;
    } else {
      _SLAs.push(SLA);
    }

    reset({
      SLAs: _SLAs,
      followsCustomerSla: data.followsCustomerSla,
      activityType: null,
      responseTime: "",
      attendedTime: "",
      resolutionTime: "",
    });

    setCurrentEditing(null);
  };

  // Grid actions
  const editCell = ({ dataItem, dataIndex }) => (
    <td>
      <Flex
        cursor="pointer"
        title="Edit"
        onClick={() => {
          reset({
            SLAs: getValues("SLAs"),
            followsCustomerSla: getValues("followsCustomerSla"),
            activityType: dataItem.activityType,

            responseTime: dataItem.responseTime,
            attendedTime: dataItem.attendedTime,
            resolutionTime: dataItem.resolutionTime,
          });
          setCurrentEditing(dataIndex);
        }}
      >
        <FaEdit fontSize="18px" />
      </Flex>
    </td>
  );

  const deleteCell = ({ dataIndex }) => (
    <td>
      <Flex cursor="pointer" title="Delete" onClick={() => remove(dataIndex)}>
        <MdDeleteSweep fontSize="18px" color="red" />
      </Flex>
    </td>
  );

  const cancelEditing = () => {
    reset({
      SLAs: getValues("SLAs"),
      followsCustomerSla: getValues("followsCustomerSla"),
      activityType: null,
      responseTime: "",
      attendedTime: 0,
      resolutionTime: "",
    });
    setCurrentEditing(null);
  };

  return (
    <form onSubmit={handleSubmit(submitInfo)}>
      <Flex w="100%" justify="space-between" mb={4}>
        <Heading>Site SLA</Heading>
      </Flex>
      <Flex w="100%" mb={4}>
        <Controller
          name="followsCustomerSla"
          control={control}
          render={({ field }) => (
            <Checkbox
              isChecked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
            >
              Follow Customer SLA
            </Checkbox>
          )}
        />
      </Flex>

      {/* SLA Form Fields */}

      <ReactHookFormDropdown
        {...generalFieldProps}
        isRequired
        filterable
        label="Activity Type"
        name="activityType"
        // id="id"
        // text="text"
        data={ACTIVITY_TYPES}
        placeholder="Select Activity Type"
        rules={formValidationRules.activityType}
      />

      <NumericInput
        {...generalFieldProps}
        isRequired
        name="responseTime"
        label="Response Time (mins)"
        rules={formValidationRules.responseTime}
      />
      {/* <NumericInput
        {...generalFieldProps}
        isRequired
        name="attendedTime"
        label="Attended Time (mins)"
        rules={formValidationRules.attendedTime}
      /> */}
      <NumericInput
        {...generalFieldProps}
        isRequired
        name="resolutionTime"
        label="Resolution Time (mins)"
        rules={formValidationRules.resolutionTime}
      />

      {/* SLA Table */}
      <Grid data={SLAfields} style={{ width: "100%" }}>
        {/* <GridColumn title="Service Type" field="serviceType" /> */}
        <GridColumn title="Activity Type" field="activityType" />
        <GridColumn title="Response Time" field="responseTime" />
        {/* <GridColumn title="Attended Time" field="attendedTime" /> */}
        <GridColumn title="Resolution Time" field="resolutionTime" />
        <GridColumn title="Edit" cell={editCell} width={120} />
        <GridColumn title="Delete" cell={deleteCell} width={120} />
      </Grid>

      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          gap: "16px",
        }}
      >
        {currentEditing !== null && (
          <Button type="button" onClick={cancelEditing}>
            Cancel
          </Button>
        )}
        <Button type="submit">
          {currentEditing !== null ? "Update SLA" : "Add SLA"}
        </Button>
      </div>
    </form>
  );
}
